/**
 * Notices when the server has ended this browser's session, from any API
 * call, and shows one clear "please refresh" message.
 *
 * Why: when a password is changed or reset, or the account is deleted, the
 * authorizer refuses this session's tokens. API Gateway answers those calls
 * with 403 {"message":"Access Denied"} (the ACCESS_DENIED gateway response).
 * Each feature handled that differently ("Error: Match not created",
 * "Please correct the errors", ...), which didn't say what happened.
 *
 * installSessionGuard wraps window.fetch once at startup. Responses aren't
 * changed: the caller still gets the same response (we read a clone).
 *
 * No imports on purpose (leaf module), so it can't create an import cycle.
 */

// The authorizer's denial, as configured on dev, and API Gateway's default
// wording (in case an environment still uses the default gateway response).
const isAuthorizerDenial = (status, body) =>
  status === 403 &&
  typeof body?.message === "string" &&
  (body.message === "Access Denied" || body.message.includes("explicit deny"));

let installed = false;
let sessionEnded = false;

/**
 * @param {{apiURL: string, onSessionEnded: function}}
 * * apiURL: only calls to this API are checked
 * * onSessionEnded: called once, the first time a denial is seen
 */
export const installSessionGuard = ({ apiURL, onSessionEnded }) => {
  if (installed || typeof window.fetch !== "function" || !apiURL) return;
  installed = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = async (resource, init) => {
    const response = await originalFetch(resource, init);
    const url = typeof resource === "string" ? resource : resource?.url ?? "";
    if (!sessionEnded && response.status === 403 && url.startsWith(apiURL)) {
      try {
        const body = await response.clone().json();
        if (isAuthorizerDenial(response.status, body)) {
          sessionEnded = true;
          onSessionEnded();
        }
      } catch (error) {
        // not JSON: not the authorizer's denial
      }
    }
    return response;
  };
};
